import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const { FiSave, FiBook, FiAlertTriangle, FiSend, FiLock, FiAlertCircle, FiRefreshCw, FiTrash, FiUsers, FiFilter } = FiIcons;

const FailureReport = () => {
  const { state, dispatch, studentService, courseService, assessmentService, performanceService } = useApp();
  const [failureData, setFailureData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [transmitLoading, setTransmitLoading] = useState(false);
  const [reportSaved, setReportSaved] = useState(false);
  const [showTransmitWarning, setShowTransmitWarning] = useState(false);
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [currentAssessments, setCurrentAssessments] = useState([]);
  const [currentStudents, setCurrentStudents] = useState([]);
  // Add state for class group filtering
  const [studentGroups, setStudentGroups] = useState({});
  const [activeGroup, setActiveGroup] = useState('All Groups');
  const [filteredStudents, setFilteredStudents] = useState([]);

  // Only generate failure report when component is mounted and has fresh data
  useEffect(() => {
    if (state.activeCourse) {
      checkIfReportSaved();
      // Always refresh student data when component loads
      loadFreshData();
    }
  }, [state.activeCourse]);

  // Regenerate failure data when fresh data is loaded
  useEffect(() => {
    if (currentStudents.length > 0 && currentAssessments.length > 0) {
      console.log('Regenerating failure report with fresh data');
      console.log('Current assessments:', currentAssessments);
      console.log('Current students:', currentStudents);
      // Organize students by class groups
      organizeStudentGroups();
      // Initially set filtered students to all students
      setFilteredStudents(currentStudents);
    }
  }, [currentStudents, currentAssessments, lastDataRefresh]);

  // Generate failure report when filtered students change
  useEffect(() => {
    if (filteredStudents.length > 0 && currentAssessments.length > 0) {
      generateFailureReport();
    }
  }, [filteredStudents]);

  const organizeStudentGroups = () => {
    const groups = { 'All Groups': [] };
    
    currentStudents.forEach(student => {
      const group = student.classGroup || 'Ungrouped';
      const faculty = student.faculty || '';
      
      // Add to All Groups
      groups['All Groups'].push(student);
      
      // Initialize group if it doesn't exist
      if (!groups[group]) {
        groups[group] = [];
      }
      
      // Add student to their group
      groups[group].push(student);
    });
    
    setStudentGroups(groups);
  };

  const switchClassGroup = (groupName) => {
    setActiveGroup(groupName);
    
    if (groupName === 'All Groups') {
      setFilteredStudents(currentStudents);
    } else if (studentGroups[groupName]) {
      setFilteredStudents(studentGroups[groupName]);
    }
  };

  const loadFreshData = async () => {
    if (!state.activeCourse) return;
    setLoading(true);
    try {
      console.log('Loading fresh data for failure report...');
      
      // Load both assessments and students in parallel
      const [assessmentsResult, studentsResult] = await Promise.all([
        assessmentService.getAssessments(state.activeCourse.id),
        studentService.getStudents(state.activeCourse.id)
      ]);

      if (assessmentsResult.error) throw new Error(assessmentsResult.error);
      if (studentsResult.error) throw new Error(studentsResult.error);

      // Process assessments
      let formattedAssessments = [];
      if (assessmentsResult.data && assessmentsResult.data.length > 0) {
        formattedAssessments = assessmentsResult.data.map(a => ({
          id: a.id,
          type: a.assessment_type,
          name: a.assessment_name,
          marks: a.marks,
          sltCI: parseFloat(a.slt_ci),
          clo: a.clo_mapping
        }));
        console.log('Fresh assessments loaded:', formattedAssessments);
      }

      // Process students
      let formattedStudents = [];
      if (studentsResult.data && studentsResult.data.length > 0) {
        formattedStudents = studentsResult.data.map(student => {
          const marks = {};
          // Convert student marks from array to object format
          if (student.student_marks_clomapper && student.student_marks_clomapper.length > 0) {
            student.student_marks_clomapper.forEach(mark => {
              marks[mark.assessment_id.toString()] = mark.marks;
            });
          }
          return {
            id: student.id,
            matricId: student.matric_id,
            name: student.name,
            marks: marks,
            classGroup: student.class_group || 'Ungrouped',
            faculty: student.faculty || ''
          };
        });
        console.log('Fresh students loaded:', formattedStudents.length, 'students');
        console.log('Sample student marks:', formattedStudents[0]?.marks);
      }

      // Update local state
      setCurrentAssessments(formattedAssessments);
      setCurrentStudents(formattedStudents);

      // Update global state
      dispatch({ type: 'SET_ASSESSMENT_PLAN', payload: formattedAssessments });
      dispatch({ type: 'SET_STUDENTS_DATA', payload: formattedStudents });

      setLastDataRefresh(new Date());
    } catch (error) {
      console.error('Error loading fresh data:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await loadFreshData();
  };

  const checkIfReportSaved = async () => {
    if (!state.activeCourse) return;
    try {
      const { data, error } = await studentService.getFailureReports(state.activeCourse.id);
      if (!error && data && data.length > 0) {
        setReportSaved(true);
        
        // Load saved failure data but recalculate totals with fresh data
        const savedFailureData = data.map(report => ({
          id: report.student_name, // Use name as ID for consistency
          studentName: report.student_name,
          matricNo: report.matric_no,
          failureStatus: report.failure_status,
          reason: report.reason,
          totalMarks: 0, // Will be recalculated
          classGroup: report.class_group || 'Ungrouped',
          faculty: report.faculty || ''
        }));
        setFailureData(savedFailureData);
      } else {
        // Even if no failure data exists, we should mark report as "saved" 
        // if we have student data available for transmission
        if (currentStudents.length > 0 && currentAssessments.length > 0) {
          setReportSaved(true);
        }
      }
    } catch (error) {
      console.error('Error checking report status:', error);
    }
  };

  const calculateTotal = (student, assessments = currentAssessments) => {
    if (!assessments || assessments.length === 0) {
      console.warn('No assessments available for calculation');
      return 0;
    }

    if (!student.marks || typeof student.marks !== 'object') {
      console.warn('Invalid marks data for student:', student.name);
      return 0;
    }

    console.log(`Calculating total for ${student.name}:`);
    console.log('Student marks:', student.marks);
    console.log('Available assessments:', assessments.map(a => ({ id: a.id, name: a.name, marks: a.marks })));

    const total = assessments.reduce((sum, assessment) => {
      const assessmentId = assessment.id.toString();
      const studentMark = student.marks[assessmentId];
      
      if (studentMark !== undefined && studentMark !== null && studentMark !== '') {
        const numericMark = parseFloat(studentMark);
        if (!isNaN(numericMark)) {
          console.log(`  ${assessment.name} (${assessmentId}): ${numericMark}%`);
          return sum + numericMark;
        }
      }
      console.log(`  ${assessment.name} (${assessmentId}): 0% (no mark or invalid)`);
      return sum;
    }, 0);

    console.log(`  Total for ${student.name}: ${total}%`);
    return total;
  };

  const generateDefaultReason = (status, total) => {
    switch (status) {
      case 'Fail (F)':
        return `Total marks: ${total.toFixed(1)}% (Below passing grade of 40%)`;
      case 'Barred (Y)':
        return 'Barred from entering final examination hall due to attendance below 80%';
      case 'Absent (Z)':
        return 'Absent during final examination';
      default:
        return '';
    }
  };

  const generateFailureReport = () => {
    console.log('===GENERATING FAILURE REPORT===');
    console.log('Filtered students:', filteredStudents.length);
    console.log('Current assessments:', currentAssessments.length);

    if (filteredStudents.length === 0 || currentAssessments.length === 0) {
      console.warn('Insufficient data for failure report generation');
      setFailureData([]);
      return;
    }

    const failingStudents = [];

    filteredStudents.forEach(student => {
      const total = calculateTotal(student, currentAssessments);
      console.log(`Student ${student.name}: Total=${total}%`);

      // Only include students who scored below 40% (failed)
      if (total < 40) {
        console.log(`  -> Adding ${student.name} to failure report (${total}%)`);
        failingStudents.push({
          id: student.id,
          studentName: student.name,
          matricNo: student.matricId,
          failureStatus: 'Fail (F)', // Default to Fail (F) for students below 40%
          reason: generateDefaultReason('Fail (F)', total),
          totalMarks: total,
          classGroup: student.classGroup || activeGroup || 'Ungrouped',
          faculty: student.faculty || ''
        });
      } else {
        console.log(`  -> ${student.name} passed with ${total}% (not included in failure report)`);
      }
    });

    console.log('Generated failure data:', failingStudents.length, 'failing students');
    console.log('Failure data details:', failingStudents);
    setFailureData(failingStudents);
    
    // Mark report as saved if we have course data, regardless of failure records
    if (currentStudents.length > 0 && currentAssessments.length > 0) {
      setReportSaved(true);
    }
  };

  const updateFailureRecord = (id, field, value) => {
    setFailureData(failureData.map(record => {
      if (record.id === id) {
        const updatedRecord = { ...record, [field]: value };
        
        // Update reason when status changes
        if (field === 'failureStatus') {
          updatedRecord.reason = generateDefaultReason(value, record.totalMarks);
        }
        
        return updatedRecord;
      }
      return record;
    }));
  };

  const addManualFailureRecord = () => {
    const newRecord = {
      id: `manual_${Date.now()}`,
      studentName: '',
      matricNo: '',
      failureStatus: 'Barred (Y)',
      reason: generateDefaultReason('Barred (Y)', 0),
      totalMarks: 0,
      isManual: true,
      classGroup: activeGroup === 'All Groups' ? 'Ungrouped' : activeGroup,
      faculty: activeGroup !== 'All Groups' && studentGroups[activeGroup]?.faculty || ''
    };
    setFailureData([...failureData, newRecord]);
  };

  const removeFailureRecord = (id) => {
    setFailureData(failureData.filter(record => record.id !== id));
  };

  const saveData = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    // Allow saving even with no failure records
    if (failureData.length > 0) {
      // Validate that all records have required data if there are failure records
      const invalidRecords = failureData.filter(record => 
        !record.studentName.trim() || !record.matricNo.trim() || !record.failureStatus
      );

      if (invalidRecords.length > 0) {
        alert('Please fill in all required fields (Student Name, Matric No, and Failure Status) for all records');
        return;
      }
    }

    setSaveLoading(true);
    try {
      if (failureData.length > 0) {
        // Save failure records if they exist
        const { error } = await studentService.saveFailureReports(state.activeCourse.id, failureData);
        if (error) throw new Error(error);
      } else {
        // Save empty failure report (indicating no failures)
        const { error } = await studentService.saveFailureReports(state.activeCourse.id, []);
        if (error) throw new Error(error);
      }

      setReportSaved(true);
      alert(failureData.length > 0 ? 'Failure report saved successfully!' : 'Report saved successfully - no failing students!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving failure report: ' + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleTransmitReport = () => {
    // Allow transmission regardless of failure records if we have student data
    if (!reportSaved && currentStudents.length === 0) {
      alert('Please ensure you have student data available before transmitting.');
      return;
    }
    setShowTransmitWarning(true);
  };

  const confirmTransmitReport = async () => {
    setTransmitLoading(true);
    setShowTransmitWarning(false);
    try {
      // Ensure failure report is saved before transmitting
      if (!reportSaved) {
        await saveData();
      }

      // Generate complete course report
      const completeReport = await generateCompleteReport();

      // Update course status to finalized
      const { error } = await courseService.finalizeCourse(
        state.activeCourse.id,
        completeReport
      );

      if (error) throw new Error(error);

      // Add to archived reports
      const archivedReport = {
        id: state.activeCourse.id,
        courseName: state.activeCourse.course_name || state.activeCourse.courseName,
        courseCode: state.activeCourse.course_code || state.activeCourse.courseCode,
        programme: state.activeCourse.programme,
        faculty: state.activeCourse.faculty,
        semester: state.activeCourse.semester,
        session: state.activeCourse.session,
        completedAt: new Date().toISOString(),
        status: 'finalized',
        report: completeReport
      };

      dispatch({ type: 'COMPLETE_COURSE', payload: archivedReport });
      dispatch({ type: 'UPDATE_COURSE', payload: { ...state.activeCourse, status: 'finalized' } });

      alert('Report transmitted successfully! The course has been finalized and moved to archives. No further edits are allowed.');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error transmitting report: ' + error.message);
    } finally {
      setTransmitLoading(false);
    }
  };

  const generateCompleteReport = async () => {
    const course = state.activeCourse;

    // Get all saved analysis data
    const [coursePerformance, cloAnalysis, ploAnalysis] = await Promise.all([
      performanceService.getCommentsAndSuggestions(course.id, 'course_performance'),
      performanceService.getCommentsAndSuggestions(course.id, 'clo_analysis'),
      performanceService.getCommentsAndSuggestions(course.id, 'plo_analysis')
    ]);

    // Calculate comprehensive statistics
    const statistics = calculateComprehensiveStatistics();

    // Generate grade distribution data
    const gradeDistribution = calculateGradeDistribution();

    // Generate bell curve analysis
    const bellCurveAnalysis = calculateBellCurveAnalysis(gradeDistribution);

    // Calculate CLO Achievement Data
    const cloAchievementData = calculateCLOAchievementData();

    // Calculate PLO Achievement Data
    const ploAchievementData = calculatePLOAchievementData();

    // Get CLO-PLO mapping with enhanced data
    const enhancedCLOPLOMapping = getEnhancedCLOPLOMapping();

    // Get student data by class groups
    const studentDataByGroups = getStudentDataByGroups();

    // Collect all course data for the complete report
    const report = {
      // Course Information
      courseInfo: {
        courseName: course.course_name || course.courseName,
        courseCode: course.course_code || course.courseCode,
        faculty: course.faculty,
        programme: course.programme,
        semester: course.semester,
        session: course.session,
        creditHours: course.credit_hours || course.creditHours,
        finalizedAt: new Date().toISOString(),
        reportGeneratedBy: state.profile?.name || state.user?.name || 'System User',
        reportGeneratedDate: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      },

      // Academic Data
      assessmentPlan: currentAssessments.map(assessment => ({
        ...assessment,
        cloStatement: state.cloStatements[assessment.clo] || 'Not defined',
        ploMappings: getPLOMappingsForCLO(assessment.clo)
      })),

      cloStatements: state.cloStatements,
      ploStatements: state.ploStatements,
      cloMapping: state.cloMapping,
      ploMapping: state.ploMapping,
      enhancedCLOPLOMapping: enhancedCLOPLOMapping,
      attainmentTargets: state.attainmentTargets,

      // Student Data
      studentsData: currentStudents,
      studentDataByGroups: studentDataByGroups,
      failureReport: failureData,

      // Statistical Analysis
      statistics: statistics,
      gradeDistribution: gradeDistribution,
      bellCurveAnalysis: bellCurveAnalysis,

      // Performance Analysis
      cloAchievementData: cloAchievementData,
      ploAchievementData: ploAchievementData,

      // Analysis Comments
      coursePerformance: {
        comments: coursePerformance.data?.comments || '',
        suggestions: coursePerformance.data?.suggestions || []
      },
      cloAnalysis: {
        comments: cloAnalysis.data?.comments || '',
        suggestions: cloAnalysis.data?.suggestions || []
      },
      ploAnalysis: {
        comments: ploAnalysis.data?.comments || '',
        suggestions: ploAnalysis.data?.suggestions || []
      },

      // Signatures
      signatures: {
        courseCoordinator: {
          name: state.profile?.name || state.user?.name || '',
          position: state.profile?.position || 'Course Coordinator',
          faculty: state.profile?.faculty || course.faculty,
          department: state.profile?.department || '',
          signatureDate: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        },
        programmeCoordinator: {
          name: '', // To be filled by programme coordinator
          position: 'Programme Coordinator',
          faculty: course.faculty,
          department: '',
          signatureDate: '' // To be filled when signed
        }
      }
    };

    return report;
  };

  // Helper functions for comprehensive analysis
  const calculateComprehensiveStatistics = () => {
    const totalStudents = currentStudents.length;
    const failingStudents = failureData.length;
    const passingStudents = totalStudents - failingStudents;
    
    // Calculate total marks for all students
    const studentTotals = currentStudents.map(student => ({
      ...student,
      totalMarks: calculateTotal(student, currentAssessments)
    }));

    const averageScore = studentTotals.length > 0 ? 
      studentTotals.reduce((sum, student) => sum + student.totalMarks, 0) / studentTotals.length : 0;

    const highestScore = studentTotals.length > 0 ? 
      Math.max(...studentTotals.map(s => s.totalMarks)) : 0;

    const lowestScore = studentTotals.length > 0 ? 
      Math.min(...studentTotals.map(s => s.totalMarks)) : 0;

    return {
      totalStudents,
      failingStudents,
      passingStudents,
      passRate: totalStudents > 0 ? ((passingStudents / totalStudents) * 100).toFixed(1) : '0',
      failureRate: totalStudents > 0 ? ((failingStudents / totalStudents) * 100).toFixed(1) : '0',
      averageScore: averageScore.toFixed(1),
      highestScore: highestScore.toFixed(1),
      lowestScore: lowestScore.toFixed(1),
      totalAssessments: currentAssessments.length,
      totalWeightage: currentAssessments.reduce((sum, assessment) => sum + assessment.marks, 0)
    };
  };

  const calculateGradeDistribution = () => {
    const gradeRanges = [
      { grade: 'A+', min: 90, max: 100, gpa: 4.000, count: 0 },
      { grade: 'A', min: 80, max: 89, gpa: 4.000, count: 0 },
      { grade: 'A-', min: 75, max: 79, gpa: 3.700, count: 0 },
      { grade: 'B+', min: 70, max: 74, gpa: 3.300, count: 0 },
      { grade: 'B', min: 65, max: 69, gpa: 3.000, count: 0 },
      { grade: 'B-', min: 60, max: 64, gpa: 2.700, count: 0 },
      { grade: 'C+', min: 55, max: 59, gpa: 2.300, count: 0 },
      { grade: 'C', min: 50, max: 54, gpa: 2.000, count: 0 },
      { grade: 'C-', min: 45, max: 49, gpa: 1.700, count: 0 },
      { grade: 'D', min: 40, max: 44, gpa: 1.000, count: 0 },
      { grade: 'F', min: 0, max: 39, gpa: 0.000, count: 0 }
    ];

    currentStudents.forEach(student => {
      const total = calculateTotal(student, currentAssessments);
      const gradeRange = gradeRanges.find(range => total >= range.min && total <= range.max);
      if (gradeRange) {
        gradeRange.count++;
      }
    });

    return gradeRanges;
  };

  const calculateBellCurveAnalysis = (gradeDistribution) => {
    const total = gradeDistribution.reduce((sum, grade) => sum + grade.count, 0);
    if (total === 0) return {};

    const highGrades = gradeDistribution
      .filter(grade => ['A+', 'A', 'A-'].includes(grade.grade))
      .reduce((sum, grade) => sum + grade.count, 0);

    const midGrades = gradeDistribution
      .filter(grade => ['B+', 'B', 'B-', 'C+', 'C'].includes(grade.grade))
      .reduce((sum, grade) => sum + grade.count, 0);

    const lowGrades = gradeDistribution
      .filter(grade => ['C-', 'D', 'F'].includes(grade.grade))
      .reduce((sum, grade) => sum + grade.count, 0);

    return {
      total,
      highGrades,
      midGrades,
      lowGrades,
      highPercentage: Math.round((highGrades / total) * 100),
      midPercentage: Math.round((midGrades / total) * 100),
      lowPercentage: Math.round((lowGrades / total) * 100),
      distributionPattern: midGrades >= (total * 0.6) ? "Normal distribution" :
                          highGrades >= (total * 0.5) ? "Positive skew" :
                          lowGrades >= (total * 0.5) ? "Negative skew" : "Irregular distribution"
    };
  };

  const calculateCLOAchievementData = () => {
    const cloAchievementData = [];
    const uniqueCLOs = [...new Set(currentAssessments.map(assessment => assessment.clo))];

    uniqueCLOs.forEach(cloCode => {
      const cloAssessments = currentAssessments.filter(assessment => assessment.clo === cloCode);
      if (cloAssessments.length === 0) return;

      const totalWeightage = cloAssessments.reduce((sum, assessment) => sum + assessment.marks, 0);
      let studentsAchievedCLO = 0;
      const totalStudents = currentStudents.length;
      const targetPercentage = state.attainmentTargets.cloTargets[cloCode] || 50;

      currentStudents.forEach(student => {
        let studentCLOMarks = 0;
        let studentCLOMaxMarks = 0;

        cloAssessments.forEach(assessment => {
          const studentMark = student.marks[assessment.id] || 0;
          const maxMark = assessment.marks;
          studentCLOMarks += studentMark;
          studentCLOMaxMarks += maxMark;
        });

        const cloPercentage = studentCLOMaxMarks > 0 ? (studentCLOMarks / studentCLOMaxMarks) * 100 : 0;
        if (cloPercentage >= targetPercentage) {
          studentsAchievedCLO++;
        }
      });

      const achievementPercentage = totalStudents > 0 ? Math.round((studentsAchievedCLO / totalStudents) * 100) : 0;

      cloAchievementData.push({
        clo: cloCode,
        statement: state.cloStatements[cloCode] || 'Not defined',
        weightage: totalWeightage,
        attainment: achievementPercentage,
        targetPercentage: targetPercentage,
        status: achievementPercentage >= targetPercentage ? 'Achieved' : 'Not Achieved',
        studentsAchieved: studentsAchievedCLO,
        totalStudents: totalStudents
      });
    });

    return cloAchievementData;
  };

  const calculatePLOAchievementData = () => {
    const ploAchievementData = [];
    const unmappedPLOs = [];

    Object.entries(state.ploStatements).forEach(([ploCode, description]) => {
      let isMapped = false;
      Object.entries(state.ploMapping).forEach(([cloCode, ploMappings]) => {
        if (ploMappings[ploCode] === true) {
          isMapped = true;
        }
      });

      if (isMapped) {
        let studentsAchievedPLO = 0;
        const totalStudents = currentStudents.length;
        const targetPercentage = state.attainmentTargets.ploTargets[ploCode] || 50;

        currentStudents.forEach(student => {
          const mappedCLOs = [];
          Object.entries(state.ploMapping).forEach(([cloCode, ploMappings]) => {
            if (ploMappings[ploCode] === true) {
              mappedCLOs.push(cloCode);
            }
          });

          let cloAchievements = 0;
          let totalMappedCLOs = mappedCLOs.length;

          mappedCLOs.forEach(cloCode => {
            const cloAssessments = currentAssessments.filter(assessment => assessment.clo === cloCode);
            if (cloAssessments.length === 0) return;

            let studentCLOMarks = 0;
            let studentCLOMaxMarks = 0;

            cloAssessments.forEach(assessment => {
              const studentMark = student.marks[assessment.id] || 0;
              const maxMark = assessment.marks;
              studentCLOMarks += studentMark;
              studentCLOMaxMarks += maxMark;
            });

            const cloPercentage = studentCLOMaxMarks > 0 ? (studentCLOMarks / studentCLOMaxMarks) * 100 : 0;
            const cloTargetPercentage = state.attainmentTargets.cloTargets[cloCode] || 50;
            if (cloPercentage >= cloTargetPercentage) {
              cloAchievements++;
            }
          });

          const ploAchievementPercentage = totalMappedCLOs > 0 ? (cloAchievements / totalMappedCLOs) * 100 : 0;
          if (ploAchievementPercentage >= targetPercentage) {
            studentsAchievedPLO++;
          }
        });

        const achievementPercentage = totalStudents > 0 ? Math.round((studentsAchievedPLO / totalStudents) * 100) : 0;

        ploAchievementData.push({
          plo: ploCode,
          description: description,
          attainment: achievementPercentage,
          targetPercentage: targetPercentage,
          status: achievementPercentage >= targetPercentage ? 'Achieved' : 'Not Achieved',
          studentsAchieved: studentsAchievedPLO,
          totalStudents: totalStudents
        });
      } else {
        unmappedPLOs.push({
          plo: ploCode,
          description: description,
          status: 'Not Applicable',
          reason: 'Not mapped to any CLO in this course'
        });
      }
    });

    return { mappedPLOs: ploAchievementData, unmappedPLOs: unmappedPLOs };
  };

  const getEnhancedCLOPLOMapping = () => {
    const mapping = [];
    
    Object.entries(state.ploMapping).forEach(([cloCode, ploMappings]) => {
      const cloStatement = state.cloStatements[cloCode] || 'Not defined';
      const mappedPLOs = [];
      
      Object.entries(ploMappings).forEach(([ploCode, isMapped]) => {
        if (isMapped) {
          mappedPLOs.push({
            code: ploCode,
            statement: state.ploStatements[ploCode] || 'Not defined'
          });
        }
      });

      mapping.push({
        cloCode: cloCode,
        cloStatement: cloStatement,
        mappedPLOs: mappedPLOs
      });
    });

    return mapping;
  };

  const getStudentDataByGroups = () => {
    const groupData = {};
    
    Object.keys(studentGroups).forEach(groupName => {
      if (groupName !== 'All Groups') {
        const students = studentGroups[groupName];
        const facultyName = students[0]?.faculty || '';
        
        // Calculate statistics for this group
        const failingStudents = students.filter(student => calculateTotal(student, currentAssessments) < 40).length;
        const passingStudents = students.length - failingStudents;
        
        // Calculate grade distribution for this group
        const gradeRanges = [
          { grade: 'A+', min: 90, max: 100, count: 0 },
          { grade: 'A', min: 80, max: 89, count: 0 },
          { grade: 'A-', min: 75, max: 79, count: 0 },
          { grade: 'B+', min: 70, max: 74, count: 0 },
          { grade: 'B', min: 65, max: 69, count: 0 },
          { grade: 'B-', min: 60, max: 64, count: 0 },
          { grade: 'C+', min: 55, max: 59, count: 0 },
          { grade: 'C', min: 50, max: 54, count: 0 },
          { grade: 'C-', min: 45, max: 49, count: 0 },
          { grade: 'D', min: 40, max: 44, count: 0 },
          { grade: 'F', min: 0, max: 39, count: 0 }
        ];

        students.forEach(student => {
          const total = calculateTotal(student, currentAssessments);
          const gradeRange = gradeRanges.find(range => total >= range.min && total <= range.max);
          if (gradeRange) {
            gradeRange.count++;
          }
        });
        
        // Calculate average score for this group
        const averageScore = students.length > 0 ? 
          students.reduce((sum, student) => sum + calculateTotal(student, currentAssessments), 0) / students.length : 0;
        
        groupData[groupName] = {
          name: groupName,
          faculty: facultyName,
          studentCount: students.length,
          failingStudents,
          passingStudents,
          passRate: students.length > 0 ? ((passingStudents / students.length) * 100).toFixed(1) : '0',
          averageScore: averageScore.toFixed(1),
          gradeDistribution: gradeRanges
        };
      }
    });
    
    return groupData;
  };

  const getPLOMappingsForCLO = (cloCode) => {
    const mappings = [];
    if (state.ploMapping[cloCode]) {
      Object.entries(state.ploMapping[cloCode]).forEach(([ploCode, isMapped]) => {
        if (isMapped) {
          mappings.push({
            code: ploCode,
            statement: state.ploStatements[ploCode] || 'Not defined'
          });
        }
      });
    }
    return mappings;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Fail (F)':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'Absent (Z)':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Barred (Y)':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusCounts = () => {
    const counts = {
      fail: failureData.filter(record => record.failureStatus === 'Fail (F)').length,
      absent: failureData.filter(record => record.failureStatus === 'Absent (Z)').length,
      barred: failureData.filter(record => record.failureStatus === 'Barred (Y)').length
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Check if course is finalized
  const isFinalized = state.activeCourse?.status === 'finalized';

  // Check if we can transmit - allow transmission if we have student data
  const canTransmit = currentStudents.length > 0 && currentAssessments.length > 0;

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to generate failure report.</p>
        </div>
      </motion.div>
    );
  }

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading fresh data...</p>
        </div>
      </motion.div>
    );
  }

  if (currentStudents.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-8">
            <SafeIcon icon={FiAlertTriangle} className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Student Data Available</h3>
            <p className="text-gray-600 mb-4">
              To generate a failure report, you need student marks data first.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <h4 className="font-semibold text-yellow-900 mb-2">Required Steps:</h4>
              <ol className="text-yellow-800 space-y-1 list-decimal list-inside">
                <li>Create and save an Assessment Plan</li>
                <li>Enter student marks in the Marks Entry section</li>
                <li>Return here to generate the failure report</li>
              </ol>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Course Info Header */}
      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-red-900">
            {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
          </h2>
          <button
            onClick={refreshData}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm"
            title="Refresh data"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            <span>Refresh Data</span>
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <span className="font-medium text-red-700">Faculty:</span>
            <p className="text-red-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-red-700">Programme:</span>
            <p className="text-red-600">{state.activeCourse.programme}</p>
          </div>
          <div>
            <span className="font-medium text-red-700">Semester:</span>
            <p className="text-red-600">{state.activeCourse.semester}</p>
          </div>
          <div>
            <span className="font-medium text-red-700">Session:</span>
            <p className="text-red-600">{state.activeCourse.session}</p>
          </div>
          <div>
            <span className="font-medium text-red-700">Total Students:</span>
            <p className="text-red-600">{filteredStudents.length}</p>
          </div>
          <div>
            <span className="font-medium text-red-700">Failing Students:</span>
            <p className="text-red-600">{failureData.length}</p>
          </div>
        </div>
        {lastDataRefresh && (
          <div className="mt-2 text-xs text-red-600">
            Last refreshed: {lastDataRefresh.toLocaleString()}
          </div>
        )}
      </div>

      {/* Class Group Selection Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-red-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Filter by Class Group</h2>
          </div>
          <div className="text-sm text-red-600 font-medium">
            {Object.keys(studentGroups).length > 0 && (
              <span>{Object.keys(studentGroups).length - 1} group(s) available</span>
            )}
          </div>
        </div>
        
        {/* Class Group Tabs */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {Object.keys(studentGroups).map(groupName => (
              <button
                key={groupName}
                onClick={() => switchClassGroup(groupName)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeGroup === groupName 
                    ? 'bg-red-600 text-white'
                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                }`}
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                  <span>{groupName}</span>
                  <span className="ml-2 bg-white text-red-800 text-xs px-2 py-0.5 rounded-full">
                    {studentGroups[groupName].length}
                  </span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Active Group Info */}
          {activeGroup && activeGroup !== 'All Groups' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-red-900">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 inline mr-2" />
                    {activeGroup} Class Group
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    Showing failure report for {studentGroups[activeGroup]?.length || 0} students
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Finalized Warning */}
      {isFinalized && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiLock} className="w-6 h-6 text-purple-600" />
            <div>
              <h3 className="font-semibold text-purple-900">Course Finalized</h3>
              <p className="text-purple-800 text-sm">
                This course has been finalized and transmitted. No further edits are allowed. 
                The complete report has been archived and can be viewed in the Archived Reports section.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Failure Report
          {activeGroup !== 'All Groups' && (
            <span className="ml-2 text-lg text-red-600">({activeGroup})</span>
          )}
        </h2>

        {/* Data Synchronization */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">📊 Data Synchronization</h4>
          <p className="text-blue-800 text-sm">
            This report is automatically generated based on the latest student marks and assessments. 
            Only students with total marks below 40% are automatically included as failed students. 
            Use the "Refresh Data" button above to ensure you're working with the most current information.
          </p>
          {activeGroup !== 'All Groups' && (
            <p className="text-blue-800 text-sm mt-2">
              <strong>Note:</strong> Currently showing failure report filtered by {activeGroup} class group. 
              This allows you to view and manage failure records for specific groups of students.
            </p>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800">Failed Students</h3>
            <p className="text-2xl font-bold text-red-600">{statusCounts.fail}</p>
            <p className="text-sm text-red-600">Below 40% marks</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800">Absent Students</h3>
            <p className="text-2xl font-bold text-yellow-600">{statusCounts.absent}</p>
            <p className="text-sm text-yellow-600">Missed final exam</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h3 className="font-semibold text-orange-800">Barred Students</h3>
            <p className="text-2xl font-bold text-orange-600">{statusCounts.barred}</p>
            <p className="text-sm text-orange-600">Attendance issues</p>
          </div>
        </div>

        {failureData.length === 0 ? (
          <div className="text-center py-8 bg-green-50 border border-green-200 rounded-lg">
            <SafeIcon icon={FiBook} className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">🎉 No Failing Students!</h3>
            <p className="text-green-700">
              {activeGroup === 'All Groups' ? 
                "Congratulations! All students have achieved passing grades (≥40%)." :
                `Congratulations! All students in the ${activeGroup} class group have achieved passing grades (≥40%).`
              }
            </p>
            {!isFinalized && (
              <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={addManualFailureRecord}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Add Manual Failure Record
                </button>
                <button
                  onClick={saveData}
                  disabled={saveLoading}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>{saveLoading ? 'Saving...' : 'Save Report (No Failures)'}</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Auto-generation Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">📊 Failure Report Logic</h4>
              <p className="text-blue-800 text-sm mb-2">
                This report automatically includes only students who failed (total marks below 40%):
              </p>
              <ul className="text-blue-800 text-sm space-y-1 list-disc list-inside">
                <li><strong>Fail (F):</strong> Students with total marks below 40% (automatically detected)</li>
                <li><strong>Absent (Z):</strong> Students who missed the final examination (manual selection)</li>
                <li><strong>Barred (Y):</strong> Students with attendance below 80% (manual selection)</li>
              </ul>
              <p className="text-blue-800 text-sm mt-2">
                You can modify the failure status and add specific reasons for each student below.
              </p>
            </div>

            {/* Failure Records Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">Student Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Matric No</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Class Group</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Total Marks</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Failure Status</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">State Reason</th>
                    {!isFinalized && (
                      <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {failureData.map((record) => (
                    <tr key={record.id}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">
                        {record.isManual ? (
                          <input
                            type="text"
                            value={record.studentName}
                            onChange={(e) => updateFailureRecord(record.id, 'studentName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Student Name"
                            disabled={isFinalized}
                          />
                        ) : (
                          record.studentName
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {record.isManual ? (
                          <input
                            type="text"
                            value={record.matricNo}
                            onChange={(e) => updateFailureRecord(record.id, 'matricNo', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Matric No"
                            disabled={isFinalized}
                          />
                        ) : (
                          record.matricNo
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {record.classGroup || 'Ungrouped'}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          record.totalMarks < 40 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {record.isManual ? 'N/A' : `${record.totalMarks.toFixed(1)}%`}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <select
                          value={record.failureStatus}
                          onChange={(e) => updateFailureRecord(record.id, 'failureStatus', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${getStatusColor(record.failureStatus)}`}
                          disabled={isFinalized}
                        >
                          <option value="Fail (F)">Fail (F)</option>
                          <option value="Absent (Z)">Absent (Z)</option>
                          <option value="Barred (Y)">Barred (Y)</option>
                        </select>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <textarea
                          value={record.reason}
                          onChange={(e) => updateFailureRecord(record.id, 'reason', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="State the specific reason for failure"
                          rows="2"
                          disabled={isFinalized}
                        />
                      </td>
                      {!isFinalized && (
                        <td className="border border-gray-300 px-4 py-2">
                          {record.isManual && (
                            <button
                              onClick={() => removeFailureRecord(record.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove Record"
                            >
                              <SafeIcon icon={FiTrash} className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            {!isFinalized && (
              <div className="mt-6 flex justify-between">
                <button
                  onClick={addManualFailureRecord}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                  <span>Add Manual Record</span>
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={saveData}
                    disabled={saveLoading}
                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{saveLoading ? 'Saving...' : 'Save Failure Report'}</span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Transmit Button - Always show if we have data, regardless of failure records */}
        {!isFinalized && canTransmit && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleTransmitReport}
              disabled={transmitLoading}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiSend} className="w-4 h-4" />
              <span>{transmitLoading ? 'Transmitting...' : 'Transmit Report'}</span>
            </button>
          </div>
        )}

        {/* Guidelines */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Status Guidelines:</h3>
          <ul className="text-blue-800 space-y-1">
            <li><strong>Fail (F):</strong> Student attended but scored below 40% (automatically detected)</li>
            <li><strong>Absent (Z):</strong> Student did not attend the final examination (manual assignment)</li>
            <li><strong>Barred (Y):</strong> Student barred from examination due to attendance below 80% (manual assignment)</li>
          </ul>
          <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-900 mb-1">Report Transmission:</h4>
            <p className="text-blue-800 text-sm">
              You can transmit the report regardless of whether there are failing students or not. 
              The system will generate a comprehensive report including all course data, analysis, and failure information (if any).
            </p>
            <p className="text-blue-800 text-sm mt-2">
              <strong>Class Group Filtering:</strong> You can filter the failure report by class group to focus on specific student groups.
              This is particularly useful for courses with multiple sections or groups.
            </p>
          </div>
        </div>
      </div>

      {/* Transmit Warning Modal */}
      {showTransmitWarning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center space-x-3 mb-4">
              <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-500" />
              <h3 className="text-xl font-bold text-gray-900">⚠️ Final Transmission Warning</h3>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                <strong>This action is IRREVERSIBLE!</strong>
              </p>
              <p className="text-gray-600 mb-4">
                Once you transmit this report, the following will happen:
              </p>
              <ul className="text-gray-600 space-y-2 list-disc list-inside">
                <li>All course data will be finalized and locked</li>
                <li>No further edits will be allowed to any section</li>
                <li>Complete comprehensive report will be generated and archived</li>
                <li>The course status will be permanently set to "Finalized"</li>
                <li>The report will include all tables, graphs, and analysis</li>
                <li>{failureData.length > 0 ? `${failureData.length} failure records will be included` : 'No failure records (all students passed)'}</li>
              </ul>
              <p className="text-red-600 font-medium mt-4">
                Are you absolutely sure you want to proceed?
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowTransmitWarning(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmTransmitReport}
                disabled={transmitLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {transmitLoading ? 'Transmitting...' : 'Yes, Transmit Report'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default FailureReport;